import { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Container,
  FormControl,
  Grid,
  IconButton,
  Input,
  Modal,
  ModalDialog,
  ModalClose,
  Stack,
  Typography,
  CircularProgress,
  Alert,
  Divider,
  Select,
  Option,
  Textarea,
  Sheet,
} from '@mui/joy';
import {
  Add,
  Delete,
  Edit,
  Share,
  Download,
  Search,
  FilterList,
  Email,
  Phone,
  Business,
  LocationOn,
  Person,
  List as ListIcon,
} from '@mui/icons-material';
import { useAuth } from '@/context/AuthContext';
import { contactService } from '@/services/contactService';
import { contactListService } from '@/services/contactListService';
import {
  Contact,
  ContactTag,
  ContactList,
  CreateContactData,
  UpdateContactData,
  ContactFilters,
  CreateContactTagData,
  ShareContactData,
} from '@/types/contact';
import toast from 'react-hot-toast';

export default function ContactsPage() {
  const { permissions } = useAuth();
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [tags, setTags] = useState<ContactTag[]>([]);
  const [contactLists, setContactLists] = useState<ContactList[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [isShareModalOpen, setIsShareModalOpen] = useState(false);
  const [isTagModalOpen, setIsTagModalOpen] = useState(false);
  const [isAddToListModalOpen, setIsAddToListModalOpen] = useState(false);
  const [selectedContact, setSelectedContact] = useState<Contact | null>(null);
  const [selectedListId, setSelectedListId] = useState<number | null>(null);
  const [searchText, setSearchText] = useState('');
  const [filters, setFilters] = useState<ContactFilters>({
    page: 1,
    limit: 50,
    sortBy: 'last_name',
    sortOrder: 'asc',
  });
  const [pagination, setPagination] = useState({
    total: 0,
    page: 1,
    limit: 50,
    pages: 0,
  });

  const canCreate = permissions.includes('contacts.create');
  const canEdit = permissions.includes('contacts.edit');
  const canDelete = permissions.includes('contacts.delete');
  const canShare = permissions.includes('contacts.share');
  const canDownload = permissions.includes('contacts.download');

  const [formData, setFormData] = useState<CreateContactData>({
    first_name: '',
    last_name: '',
    company: '',
    job_title: '',
    email: '',
    phone: '',
    mobile: '',
    fax: '',
    website: '',
    address_street: '',
    address_city: '',
    address_state: '',
    address_postal_code: '',
    address_country: '',
    notes: '',
    birthday: '',
    tagIds: [],
  });

  const [shareData, setShareData] = useState<ShareContactData>({
    emails: [],
    message: '',
  });

  const [tagFormData, setTagFormData] = useState<CreateContactTagData>({
    name: '',
    color: '#6B7280',
  });

  useEffect(() => {
    loadContacts();
    loadTags();
    loadContactLists();
  }, [filters]);

  const loadContacts = async () => {
    try {
      setIsLoading(true);
      const response = await contactService.getContacts({
        ...filters,
        searchText: searchText || undefined,
      });
      setContacts(response.data);
      setPagination({
        total: response.total,
        page: response.page,
        limit: response.limit,
        pages: response.pages,
      });
    } catch (error: any) {
      toast.error('Failed to load contacts');
      console.error('Error loading contacts:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const loadTags = async () => {
    try {
      const tagsData = await contactService.getTags();
      setTags(tagsData);
    } catch (error: any) {
      console.error('Error loading tags:', error);
    }
  };

  const loadContactLists = async () => {
    try {
      const listsData = await contactListService.getLists();
      setContactLists(listsData);
    } catch (error: any) {
      console.error('Error loading contact lists:', error);
    }
  };

  const handleCreateContact = () => {
    setSelectedContact(null);
    setFormData({
      first_name: '',
      last_name: '',
      company: '',
      job_title: '',
      email: '',
      phone: '',
      mobile: '',
      fax: '',
      website: '',
      address_street: '',
      address_city: '',
      address_state: '',
      address_postal_code: '',
      address_country: '',
      notes: '',
      birthday: '',
      tagIds: [],
    });
    setIsFormOpen(true);
  };

  const handleEditContact = (contact: Contact) => {
    setSelectedContact(contact);
    setFormData({
      first_name: contact.first_name,
      last_name: contact.last_name,
      company: contact.company || '',
      job_title: contact.job_title || '',
      email: contact.email || '',
      phone: contact.phone || '',
      mobile: contact.mobile || '',
      fax: contact.fax || '',
      website: contact.website || '',
      address_street: contact.address_street || '',
      address_city: contact.address_city || '',
      address_state: contact.address_state || '',
      address_postal_code: contact.address_postal_code || '',
      address_country: contact.address_country || '',
      notes: contact.notes || '',
      birthday: contact.birthday || '',
      tagIds: contact.tags?.map(t => t.id) || [],
    });
    setIsFormOpen(true);
  };

  const handleSaveContact = async () => {
    try {
      if (selectedContact) {
        await contactService.updateContact(selectedContact.id, formData);
        toast.success('Contact updated successfully');
      } else {
        await contactService.createContact(formData);
        toast.success('Contact created successfully');
      }
      setIsFormOpen(false);
      loadContacts();
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Failed to save contact');
    }
  };

  const handleDeleteContact = async (id: number) => {
    if (!window.confirm('Are you sure you want to delete this contact?')) {
      return;
    }
    try {
      await contactService.deleteContact(id);
      toast.success('Contact deleted successfully');
      loadContacts();
    } catch (error: any) {
      toast.error('Failed to delete contact');
    }
  };

  const handleShareContact = (contact: Contact) => {
    setSelectedContact(contact);
    setShareData({ emails: [], message: '' });
    setIsShareModalOpen(true);
  };

  const handleSendShare = async () => {
    if (!selectedContact || shareData.emails.length === 0) {
      toast.error('Please provide at least one email address');
      return;
    }
    try {
      await contactService.shareContact(selectedContact.id, shareData);
      toast.success('Contact shared successfully');
      setIsShareModalOpen(false);
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Failed to share contact');
    }
  };

  const handleDownloadVCard = async (contact: Contact) => {
    try {
      const blob = await contactService.downloadVCard(contact.id);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${contact.first_name}_${contact.last_name}.vcf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      toast.success('vCard downloaded successfully');
    } catch (error: any) {
      toast.error('Failed to download vCard');
    }
  };

  const handleCreateTag = async () => {
    try {
      await contactService.createTag(tagFormData);
      toast.success('Tag created successfully');
      setIsTagModalOpen(false);
      setTagFormData({ name: '', color: '#6B7280' });
      loadTags();
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Failed to create tag');
    }
  };

  const handleAddToList = (contact: Contact) => {
    setSelectedContact(contact);
    setSelectedListId(null);
    setIsAddToListModalOpen(true);
  };

  const handleSaveToList = async () => {
    if (!selectedContact || !selectedListId) {
      toast.error('Please select a contact list');
      return;
    }
    try {
      await contactListService.addContactToList(selectedListId, selectedContact.id);
      toast.success('Contact added to list successfully');
      setIsAddToListModalOpen(false);
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Failed to add contact to list');
    }
  };

  const handleSearch = (value: string) => {
    setSearchText(value);
    setFilters({ ...filters, page: 1 });
  };

  return (
    <Container maxWidth="xl">
      <Box sx={{ py: 3 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 3 }}>
          <Typography level="h1" component="h1">
            Contacts
          </Typography>
          {canCreate && (
            <Button startDecorator={<Add />} onClick={handleCreateContact}>
              New Contact
            </Button>
          )}
        </Stack>

        {/* Search and Filters */}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Stack direction="row" spacing={2} alignItems="center">
              <FormControl sx={{ flex: 1 }}>
                <Input
                  placeholder="Search contacts..."
                  value={searchText}
                  onChange={(e) => handleSearch(e.target.value)}
                  startDecorator={<Search />}
                />
              </FormControl>
              <Button variant="outlined" startDecorator={<FilterList />}>
                Filters
              </Button>
            </Stack>
          </CardContent>
        </Card>

        {/* Contacts List */}
        {isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : contacts.length === 0 ? (
          <Alert color="neutral">No contacts found</Alert>
        ) : (
          <Grid container spacing={2}>
            {contacts.map((contact) => (
              <Grid xs={12} sm={6} md={4} key={contact.id}>
                <Card>
                  <CardContent>
                    <Stack direction="row" justifyContent="space-between" alignItems="start" sx={{ mb: 2 }}>
                      <Box>
                        <Typography level="title-md">
                          {contact.first_name} {contact.last_name}
                        </Typography>
                        {contact.job_title && (
                          <Typography level="body-sm" sx={{ color: 'text.secondary' }}>
                            {contact.job_title}
                          </Typography>
                        )}
                        {contact.company && (
                          <Typography level="body-sm" sx={{ color: 'text.secondary' }}>
                            <Business sx={{ fontSize: 14, mr: 0.5 }} />
                            {contact.company}
                          </Typography>
                        )}
                      </Box>
                      <Stack direction="row" spacing={0.5}>
                        {canEdit && (
                          <IconButton
                            size="sm"
                            variant="plain"
                            onClick={() => handleEditContact(contact)}
                          >
                            <Edit />
                          </IconButton>
                        )}
                        {canDelete && (
                          <IconButton
                            size="sm"
                            variant="plain"
                            color="danger"
                            onClick={() => handleDeleteContact(contact.id)}
                          >
                            <Delete />
                          </IconButton>
                        )}
                      </Stack>
                    </Stack>

                    <Divider sx={{ my: 1 }} />

                    <Stack spacing={1}>
                      {contact.email && (
                        <Typography level="body-sm">
                          <Email sx={{ fontSize: 14, mr: 0.5 }} />
                          {contact.email}
                        </Typography>
                      )}
                      {contact.phone && (
                        <Typography level="body-sm">
                          <Phone sx={{ fontSize: 14, mr: 0.5 }} />
                          {contact.phone}
                        </Typography>
                      )}
                      {contact.tags && contact.tags.length > 0 && (
                        <Stack direction="row" spacing={0.5} flexWrap="wrap" sx={{ mt: 1 }}>
                          {contact.tags.map((tag) => (
                            <Chip
                              key={tag.id}
                              size="sm"
                              sx={{ backgroundColor: tag.color, color: 'white' }}
                            >
                              {tag.name}
                            </Chip>
                          ))}
                        </Stack>
                      )}
                    </Stack>

                    <Stack direction="row" spacing={1} sx={{ mt: 2 }} flexWrap="wrap">
                      {canShare && (
                        <Button
                          size="sm"
                          variant="outlined"
                          startDecorator={<Share />}
                          onClick={() => handleShareContact(contact)}
                        >
                          Share
                        </Button>
                      )}
                      {canDownload && (
                        <Button
                          size="sm"
                          variant="outlined"
                          startDecorator={<Download />}
                          onClick={() => handleDownloadVCard(contact)}
                        >
                          Download
                        </Button>
                      )}
                      {permissions.includes('contact_lists.edit') && (
                        <Button
                          size="sm"
                          variant="outlined"
                          startDecorator={<ListIcon />}
                          onClick={() => handleAddToList(contact)}
                        >
                          Add to List
                        </Button>
                      )}
                    </Stack>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        )}

        {/* Pagination */}
        {pagination.pages > 1 && (
          <Stack direction="row" justifyContent="center" spacing={2} sx={{ mt: 3 }}>
            <Button
              variant="outlined"
              disabled={pagination.page === 1}
              onClick={() => setFilters({ ...filters, page: pagination.page - 1 })}
            >
              Previous
            </Button>
            <Typography level="body-md" sx={{ alignSelf: 'center' }}>
              Page {pagination.page} of {pagination.pages}
            </Typography>
            <Button
              variant="outlined"
              disabled={pagination.page === pagination.pages}
              onClick={() => setFilters({ ...filters, page: pagination.page + 1 })}
            >
              Next
            </Button>
          </Stack>
        )}
      </Box>

      {/* Contact Form Modal */}
      <Modal open={isFormOpen} onClose={() => setIsFormOpen(false)}>
        <ModalDialog sx={{ maxWidth: 600, maxHeight: '90vh', overflow: 'auto' }}>
          <ModalClose />
          <Typography level="h2" sx={{ mb: 2 }}>
            {selectedContact ? 'Edit Contact' : 'New Contact'}
          </Typography>
          <Stack spacing={2}>
            <FormControl required>
              <Input
                placeholder="First Name"
                value={formData.first_name}
                onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
              />
            </FormControl>
            <FormControl required>
              <Input
                placeholder="Last Name"
                value={formData.last_name}
                onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
              />
            </FormControl>
            <FormControl>
              <Input
                placeholder="Company"
                value={formData.company}
                onChange={(e) => setFormData({ ...formData, company: e.target.value })}
              />
            </FormControl>
            <FormControl>
              <Input
                placeholder="Job Title"
                value={formData.job_title}
                onChange={(e) => setFormData({ ...formData, job_title: e.target.value })}
              />
            </FormControl>
            <FormControl>
              <Input
                type="email"
                placeholder="Email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              />
            </FormControl>
            <FormControl>
              <Input
                placeholder="Phone"
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
              />
            </FormControl>
            <FormControl>
              <Input
                placeholder="Mobile"
                value={formData.mobile}
                onChange={(e) => setFormData({ ...formData, mobile: e.target.value })}
              />
            </FormControl>
            <FormControl>
              <Textarea
                placeholder="Notes"
                minRows={3}
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              />
            </FormControl>
            <Stack direction="row" spacing={2}>
              <Button onClick={handleSaveContact}>Save</Button>
              <Button variant="outlined" onClick={() => setIsFormOpen(false)}>
                Cancel
              </Button>
            </Stack>
          </Stack>
        </ModalDialog>
      </Modal>

      {/* Share Modal */}
      <Modal open={isShareModalOpen} onClose={() => setIsShareModalOpen(false)}>
        <ModalDialog>
          <ModalClose />
          <Typography level="h2" sx={{ mb: 2 }}>
            Share Contact
          </Typography>
          <Stack spacing={2}>
            <FormControl required>
              <Input
                placeholder="Email addresses (comma separated)"
                value={shareData.emails.join(', ')}
                onChange={(e) =>
                  setShareData({
                    ...shareData,
                    emails: e.target.value.split(',').map((email) => email.trim()),
                  })
                }
              />
            </FormControl>
            <FormControl>
              <Textarea
                placeholder="Optional message"
                minRows={3}
                value={shareData.message}
                onChange={(e) => setShareData({ ...shareData, message: e.target.value })}
              />
            </FormControl>
            <Stack direction="row" spacing={2}>
              <Button onClick={handleSendShare}>Send</Button>
              <Button variant="outlined" onClick={() => setIsShareModalOpen(false)}>
                Cancel
              </Button>
            </Stack>
          </Stack>
        </ModalDialog>
      </Modal>

      {/* Add to List Modal */}
      <Modal open={isAddToListModalOpen} onClose={() => setIsAddToListModalOpen(false)}>
        <ModalDialog>
          <ModalClose />
          <Typography level="h2" sx={{ mb: 2 }}>
            Add Contact to List
          </Typography>
          <Stack spacing={2}>
            <FormControl required>
              <Select
                placeholder="Select a contact list"
                value={selectedListId}
                onChange={(_, value) => setSelectedListId(value as number)}
              >
                {contactLists.map((list) => (
                  <Option key={list.id} value={list.id}>
                    {list.name} ({list.member_count || 0} contacts)
                  </Option>
                ))}
              </Select>
            </FormControl>
            <Stack direction="row" spacing={2}>
              <Button onClick={handleSaveToList}>Add to List</Button>
              <Button variant="outlined" onClick={() => setIsAddToListModalOpen(false)}>
                Cancel
              </Button>
            </Stack>
          </Stack>
        </ModalDialog>
      </Modal>
    </Container>
  );
}

